import React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export type CalloutTone = "info" | "brand" | "neutral" | "success" | "warn" | "danger";

type Props = {
  children: React.ReactNode;
  tone?: CalloutTone;
  /** Leading icon (tinted to the tone). */
  icon?: React.ReactNode;
  /** Dashed border — for placeholder / "not ready yet" notices. */
  dashed?: boolean;
  /** Inline action pushed to the right (a link or small button). */
  action?: React.ReactNode;
  /** When set, renders a trailing dismiss (X) button. */
  onDismiss?: () => void;
  dismissLabel?: string;
  /** e.g. "status" / "alert" to announce the notice to screen readers. */
  role?: string;
  className?: string;
};

/** One notice box — info/brand, neutral, success, warn or danger — with a
 *  leading icon, text, and an optional inline action or dismiss. Folds the five
 *  re-declared banners (`.vb-run-banner`, `.q-gate`, `.wb-soon`,
 *  `.vb-stale-banner`, `.wb-allclear`). */
export function Callout({
  children,
  tone = "info",
  icon,
  dashed,
  action,
  onDismiss,
  dismissLabel = "Dismiss",
  role,
  className,
}: Props) {
  return (
    <div
      role={role}
      className={cn("vb-callout", `vb-callout--${tone}`, dashed && "vb-callout--dashed", className)}
    >
      {icon != null && (
        <span className="vb-callout__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <div className="vb-callout__body">{children}</div>
      {action != null && <div className="vb-callout__action">{action}</div>}
      {onDismiss && (
        <button type="button" className="vb-callout__dismiss" onClick={onDismiss} aria-label={dismissLabel}>
          <X size={15} />
        </button>
      )}
    </div>
  );
}

export default Callout;
